/**
 * Fee products management for cart operations.
 * Automatically adds/removes fee products based on personalized item requirements.
 * 
 * @description This module handles automatic fee product management:
 * - Detects personalized items in cart that require fee products
 * - Adds required fee products to cart automatically
 * - Removes fee products when no longer needed
 * - Syncs fee product quantities with personalized item counts
 * 
 * @requires getCookie (global function)
 * 
 * @example
 * // Automatically initializes on page load
 * feeProducts.init();
 */
let feeProducts = {
  /** @type {boolean} Prevents concurrent execution */
  running: false,

  /**
   * Initializes the fee products module by fetching current cart data.
   * Only runs if not already executing.
   * 
   * @returns {void}
   * @sideEffect Sets running flag, fetches cart data, calls checkCartForFeeProduct
   */
  init: function () {
    if (!this.running) {
      this.running = true;
      fetch('/cart.js').then(res => res.json()).then(response => {
        this.checkCartForFeeProduct(response);
      });
    }
  },

  /**
   * Processes cart items to determine if fee products need to be added or removed.
   * Checks for personalized items and manages their associated fee products.
   * 
   * @param {Object} data - Cart data object from /cart.js
   * @returns {void}
   * @sideEffect May add/remove fee products via API, updates cart
   * 
   * @example
   * feeProducts.checkCartForFeeProduct(cartData);
   */
  checkCartForFeeProduct: function (data) {
    // Skip empty carts
    if (data.item_count < 1 || typeof data.items == "undefined") {
      this.running = false;
      return;
    }

    let items = data.items;
    let _this = this;

    // Track personalized items and required fee product quantities
    let personalizedItemsKeys = [];
    let feeProductsQuantities = {};

    // First pass: count required quantities for each fee product
    items.forEach(function (item) {
      // Check each of 3 possible personalization slots
      for (let i = 1; i < 4; i++) {
        // Check if item has this personalization level
        if (typeof item.properties['_personalized_' + i] != "undefined" && item.properties['_personalized_' + i] == 'true') {
          // Check if there's a fee product assigned to this personalization level
          if (typeof item.properties['_fee_product_' + i] != "undefined") {
            let id = item.properties['_fee_product_' + i];
            // Increment quantity count for this fee product
            if (feeProductsQuantities.hasOwnProperty(id)) {
              feeProductsQuantities[id]++;
            } else {
              feeProductsQuantities[id] = 1
            }
          }
        }
      }
    });

    // Second pass: process each item for fee product management
    items.forEach(function (item) {
      let feeRequired = false;
      let feeInCart = false;
      let feeProductId = null;
      let feeItemKey = null;
      let targetItemKey = null;

      // Check each personalization level for this item
      for (let i = 1; i < 4; i++) {
        // Check if this item requires personalization and has a fee product
        if (typeof item.properties['_personalized_' + i] != "undefined" && item.properties['_personalized_' + i] == 'true') {
          feeRequired = true;
          personalizedItemsKeys.push(item.key)

          if (typeof item.properties['_fee_product_' + i] != "undefined") {
            feeInCart = false;
            feeProductId = null;
            feeItemKey = null;
            targetItemKey = null;

            let id = item.properties['_fee_product_' + i];
            feeProductId = parseInt(id);
            targetItemKey = item.key;

            // Check if fee product is already in cart
            items.forEach(function (feeItem) {
              if (feeItem.variant_id == feeProductId) {
                feeInCart = true;
                feeItemKey = feeItem.key;

                // Fix quantity if it doesn't match required amount
                if (feeItem.quantity != feeProductsQuantities[feeProductId]) {
                  _this.updateItemQty(feeItemKey, feeProductsQuantities[feeProductId]);
                }
              }
            });

            // Add fee product if required and not in cart
            if (!feeInCart && feeRequired && feeProductId) {
              _this.addFeeProductToCart(feeProductId, targetItemKey);
            }

            // Remove fee product if no longer required
            if (feeInCart && !feeRequired && feeProductId) {
              _this.updateItemQty(feeItemKey, 0);
            }
          }
        }
      }
    });

    // Clean up any orphaned fee products
    this.removeWrongFeeProducts(items, personalizedItemsKeys, feeProductsQuantities);
  },

  /**
   * Adds a fee product to the cart with metadata linking it to the target item.
   * 
   * @param {number} id - The variant ID of the fee product to add
   * @param {string} targetItemKey - The cart item key of the item requiring this fee
   * @returns {void}
   * @sideEffect Makes API call to add product, dispatches cart events, may reload page
   * 
   * @example
   * feeProducts.addFeeProductToCart(123456789, 'abc123def456');
   */
  addFeeProductToCart: function (id, targetItemKey) {
    let _this = this;
    // Prepare cart item data with fee product metadata
    let data = {
      quantity: 1,
      id: id,
      properties: {
        '_is_fee_product': true,
        '_target_item_key': targetItemKey
      }
    };
    fetch('/cart/add.js', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(response => {
      // Notify other components that cart changed
      document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
      // Open cart drawer
      document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false }));
      
      // Reload on cart/checkout pages
      if (document.querySelector('body').classList.contains('template-cart') || document.querySelector('body').classList.contains('shopify-checkout')) {
        location.reload();
      }
      _this.running = false;
    });
  },

  /**
   * Updates the quantity of a cart item (including removal when qty is 0).
   * 
   * @param {string} id - The cart item key (line item identifier)
   * @param {number} [qty=1] - The new quantity (0 removes the item)
   * @returns {void}
   * @sideEffect Makes API call to update quantity, may reload page
   * 
   * @example
   * // Remove item
   * feeProducts.updateItemQty('abc123', 0);
   * 
   * // Set quantity to 3
   * feeProducts.updateItemQty('abc123', 3);
   */
  updateItemQty: function (id, qty = 1) {
    let _this = this;
    fetch('/cart/change.js', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({id: id, quantity: qty})
    }).then(response => {
      // Reload on cart/checkout pages
      if (document.querySelector('body').classList.contains('template-cart') || document.querySelector('body').classList.contains('shopify-checkout')) {
        location.reload();
      }
      _this.running = false;
    });
  },

  /**
   * Removes fee products that are no longer needed (orphaned from target items).
   * 
   * @param {Array} items - Cart items array
   * @param {Array} keys - Array of valid target item keys
   * @param {Object} feeProductsQuantities - Required quantities by fee product ID
   * @returns {void}
   * @sideEffect May call updateItemQty to remove items
   * 
   * @example
   * feeProducts.removeWrongFeeProducts(cartItems, validKeys, requiredQuantities);
   */
  removeWrongFeeProducts: function (items, keys, feeProductsQuantities) {
    let _this = this;
    let updatedItems = 0;

    // Check each fee product in cart
    items.forEach(function (item) {
      // Check if this is a fee product
      if (typeof item.properties['_is_fee_product'] != "undefined") {
        // Check if its target item is no longer in cart
        if (!keys.includes(item.properties['_target_item_key'])) {
          // Remove this orphaned fee product
          for (let i = 1; i < 4; i++) {
            let id = parseInt(item.properties['_fee_product_' + i]);
            let qty = feeProductsQuantities[id];
            if (typeof qty == "undefined") qty = 0;
            _this.updateItemQty(item.key, qty);
            updatedItems++;
          }
        }
      }
    });

    // Reset running flag if no updates were made
    if (updatedItems < 1) {
      this.running = false;
    }
  }

}

// Initialize on page load
feeProducts.init();

// Listen for cart updates to re-check fee products
document.addEventListener("cart:updated", (e) => {
  feeProducts.init();
});
